import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { PanelShell, PlnBarra, PlnBarraSep, PlnBloco, stickyTh } from './PanelShell'
import { NeuSwitch } from './NeuSwitch'
import {
  FiPlus, FiSearch, FiEdit2, FiChevronUp, FiChevronDown, FiLoader,
  FiChevronLeft, FiChevronRight, FiGrid, FiTrash2,
} from 'react-icons/fi'

interface CostCenter { id: number; code: string | null; name: string; is_active: 0 | 1 }
type Status = 'active' | 'inactive' | 'all'
type Sort = 'code' | 'name'
interface Counts { active: number; inactive: number; all: number }

const PAGE_SIZE = 10

export function CentrosDeCusto() {
  const { authenticatedFetch } = useAuth()
  const [items, setItems] = useState<CostCenter[]>([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<Counts>({ active: 0, inactive: 0, all: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>('active')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Sort>('code')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState<{ cat?: CostCenter } | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const qs = new URLSearchParams({ status, search, sort, dir, page: String(page), pageSize: String(PAGE_SIZE) })
      const r = await authenticatedFetch(`/api/cost-centers?${qs}`)
      const d = await r.json()
      if (d.success) { setItems(d.items); setTotal(d.total); setCounts(d.counts) }
      else setError(d.error || 'Falha ao carregar')
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, status, search, sort, dir, page])

  // recarrega ao mudar filtros (busca com pequeno debounce)
  useEffect(() => {
    const t = setTimeout(load, 250)
    return () => clearTimeout(t)
  }, [load])

  // volta pra página 1 quando muda busca/status
  useEffect(() => { setPage(1) }, [status, search])

  const toggleSort = (col: Sort) => {
    if (sort === col) setDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(col); setDir('asc') }
  }

  const setActive = async (cc: CostCenter, active: boolean) => {
    const r = await authenticatedFetch(`/api/cost-centers/${cc.id}`, { method: 'PUT', body: JSON.stringify({ is_active: active }) })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha'); return }
    load()
  }

  const remove = async (cc: CostCenter) => {
    if (!window.confirm(`Excluir definitivamente o centro de custo "${cc.name}"?`)) return
    const r = await authenticatedFetch(`/api/cost-centers/${cc.id}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha ao excluir'); return }
    load()
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const tabs: Array<{ key: Status; label: string; n: number }> = [
    { key: 'active', label: 'Ativos', n: counts.active },
    { key: 'inactive', label: 'Inativos', n: counts.inactive },
    { key: 'all', label: 'Todos', n: counts.all },
  ]

  const SortHead = ({ col, children }: { col: Sort; children: React.ReactNode }) => (
    <button onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 hover:text-foreground">
      {children}
      {sort === col
        ? (dir === 'asc' ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />)
        : <FiChevronUp className="w-3.5 h-3.5 opacity-20" />}
    </button>
  )

  return (
    <PanelShell
      titulo="Centros de custo"
      sub="Áreas e projetos para separar as despesas"
      icone={<FiGrid />}
      acoes={
        <button
          onClick={() => setModal({})}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:opacity-90"
        >
          <FiPlus className="w-4 h-4" /> Novo centro de custo
        </button>
      }>
      {/* Toolbar — PLANOS (21/10/2026): busca + abas de situação numa PlnBarra;
          o botão "Novo" subiu para as ações do cabeçalho. */}
      <PlnBarra>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome ou código…"
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
        <PlnBarraSep />
        {/* Tabs contadores */}
        <div className="neu-seg inline-flex items-center gap-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              status === t.key ? 'neu-relief bg-primary/10 text-primary' : 'neu-seg-btn text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({t.n})</span>
          </button>
        ))}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell).
          PLANOS (21/10/2026): dentro de PlnBloco; contagem na nota e paginação
          no `extra` do cabeçalho do bloco (antes ficavam soltas abaixo). */}
      <PlnBloco
        titulo="Centros de custo"
        nota={loading ? undefined : `${total} registro(s)${totalPages > 1 ? ` · página ${page} de ${totalPages}` : ''}`}
        extra={totalPages > 1 ? (
          <div className="flex items-center gap-1 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-border disabled:opacity-40 hover:bg-secondary">
              <FiChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-md text-sm ${n === page ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-secondary'}`}>
                {n}
              </button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-border disabled:opacity-40 hover:bg-secondary">
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : undefined}
        className="flex-1 min-h-[280px]"
      >
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-32 ${stickyTh}`}><SortHead col="code">Código</SortHead></th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}><SortHead col="name">Nome</SortHead></th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Situação</th>
              <th className={`px-4 py-2.5 font-medium w-24 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Nenhum centro de custo{search ? ' encontrado' : ''}.</td></tr>
            ) : items.map(cc => (
              <tr key={cc.id} className="neu-tr border-b border-border/60 last:border-0 hover:bg-secondary/40">
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{cc.code || '—'}</td>
                <td className="px-4 py-2.5 text-foreground">{cc.name}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                    cc.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {cc.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Editar" onClick={() => setModal({ cat: cc })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <NeuSwitch on={cc.is_active} onClick={() => setActive(cc, !cc.is_active)}
                      title={cc.is_active ? 'Inativar' : 'Reativar'} />
                    <button title="Excluir" onClick={() => remove(cc)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-secondary">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </PlnBloco>

      {modal && (
        <CostCenterModal
          cat={modal.cat}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}
    </PanelShell>
  )
}

function CostCenterModal({ cat, onClose, onSaved }: { cat?: CostCenter; onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [name, setName] = useState(cat?.name || '')
  const [code, setCode] = useState(cat?.code || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    if (!name.trim()) { setErr('Informe o nome.'); return }
    setSaving(true); setErr(null)
    try {
      const r = cat
        ? await authenticatedFetch(`/api/cost-centers/${cat.id}`, { method: 'PUT', body: JSON.stringify({ name, code }) })
        : await authenticatedFetch('/api/cost-centers', { method: 'POST', body: JSON.stringify({ name, code }) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      onSaved()
    } catch {
      setErr('Erro de conexão')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SideModal
      title={cat ? 'Editar centro de custo' : 'Novo centro de custo'}
      subtitle="Organize as despesas por área ou projeto"
      icon={FiGrid}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </>
      }
    >
      {err && <div className="text-sm text-red-500">{err}</div>}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Código (opcional)</label>
        <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()}
          className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          placeholder="Ex.: CC-01" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Nome*</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()}
          className="neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          placeholder="Ex.: Administrativo" />
      </div>
    </SideModal>
  )
}
